import React from "react";
import { X, Copy, Check, Download, Loader2, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { displayName } from "@/lib/stickerName";

export const PREVIEW_BGS = [
  { key: "queimado", label: "Rosa queimado", hex: "#A8676E" },
  { key: "blush", label: "Rosé claro", hex: "#F6E3E3" },
  { key: "branco", label: "Branco", hex: "#FFFFFF" },
  { key: "creme", label: "Creme", hex: "#F3EBDD" },
  { key: "cinza", label: "Cinza", hex: "#BDB7B4" },
  { key: "rose", label: "Rosé", hex: "#C58B90" },
  { key: "preto", label: "Preto", hex: "#2C2A29" },
];

function BgPicker({ id, bgKey, onBg }) {
  return (
    <div className="flex items-center gap-2" data-testid={`preview-bg-options-${id}`}>
      {PREVIEW_BGS.map((b) => (
        <button
          key={b.key}
          onClick={() => onBg(b.key)}
          data-testid={`preview-bg-${id}-${b.key}`}
          title={b.label}
          aria-label={`Fundo ${b.label}`}
          className={`h-7 w-7 rounded-lg border transition-transform duration-150 active:scale-90 ${
            bgKey === b.key ? "ring-2 ring-offset-2 ring-[#C58B90] border-white" : "border-black/15"
          }`}
          style={{ backgroundColor: b.hex }}
        />
      ))}
    </div>
  );
}

export function StickerPreview({ open, onOpenChange, sticker, src, loading, colorLabel, swatches, bgKey, onBg, onCopy, copying, copied, onDownload }) {
  const bg = PREVIEW_BGS.find((b) => b.key === bgKey) || PREVIEW_BGS[0];
  const close = () => onOpenChange(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`sticker-preview-${sticker.id}`}
        className="w-[calc(100%-1.5rem)] max-w-md rounded-[2rem] border-[#F2E6E7] bg-white p-5 sm:p-6 gap-0 max-h-[92vh] overflow-y-auto [&>button:last-child]:hidden"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <DialogTitle className="font-serif text-2xl sm:text-3xl font-normal truncate" data-testid={`preview-title-${sticker.id}`}>
              {displayName(sticker.name) || "\u00A0"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#7A7571] mt-1" data-testid={`preview-color-label-${sticker.id}`}>
              Cor: {colorLabel}
            </DialogDescription>
          </div>
          <button
            onClick={close}
            data-testid={`preview-close-${sticker.id}`}
            aria-label="Fechar"
            className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-[#5C5755] transition-[background-color] duration-200 hover:bg-[#F7EBEC]"
          >
            <X size={22} />
          </button>
        </div>

        <div
          className="mt-4 relative aspect-square rounded-3xl border-2 border-dashed border-[#E9C9CD] flex items-center justify-center overflow-hidden transition-[background-color] duration-200"
          style={{ backgroundColor: bg.hex }}
          data-testid={`preview-canvas-${sticker.id}`}
        >
          <img
            src={src}
            alt={sticker.name}
            decoding="async"
            data-testid={`preview-img-${sticker.id}`}
            className={`h-[88%] w-[88%] object-contain transition-opacity duration-150 ${loading ? "opacity-60" : "opacity-100"}`}
          />
          {loading && <Loader2 size={24} className="absolute animate-spin text-[#C58B90]" />}
        </div>

        <p className="mt-4 text-[11px] font-bold uppercase tracking-widest text-[#B07A7F]">Cor da figurinha</p>
        {swatches}

        <p className="mt-3 text-[11px] font-bold uppercase tracking-widest text-[#B07A7F]">Fundo da prévia</p>
        <div className="mt-1.5 px-1 py-1"><BgPicker id={sticker.id} bgKey={bg.key} onBg={onBg} /></div>
        <p className="mt-1 text-[11px] text-[#7A7571]">O fundo serve só para visualizar — a cópia sai sem fundo.</p>

        <div className="mt-5 flex items-center gap-2">
          <button
            data-testid={`preview-copy-${sticker.id}`}
            onClick={onCopy}
            disabled={copying}
            className="flex-1 h-12 rounded-full bg-[#2C2A29] text-white font-bold flex items-center justify-center gap-2 transition-[background-color,transform] duration-200 hover:bg-black active:scale-[0.97] disabled:opacity-70"
          >
            {copying ? <Loader2 size={17} className="animate-spin" /> : copied ? <Check size={17} /> : <Copy size={17} />}
            {copied ? "Copiado!" : "Copiar sticker"}
          </button>
          <button
            data-testid={`preview-download-${sticker.id}`}
            onClick={onDownload}
            aria-label="Baixar"
            title="Baixar"
            className="h-12 w-12 shrink-0 rounded-full bg-[#F7EBEC] text-[#B07A7F] flex items-center justify-center transition-[background-color] duration-200 hover:bg-[#EFD9DB]"
          >
            <Download size={18} />
          </button>
        </div>
        <button
          onClick={close}
          data-testid={`preview-back-${sticker.id}`}
          className="mt-3 mx-auto flex items-center gap-1.5 text-sm font-semibold text-[#7A7571] transition-colors duration-200 hover:text-[#2C2A29]"
        >
          <ArrowLeft size={15} /> Voltar e escolher outra figurinha
        </button>
      </DialogContent>
    </Dialog>
  );
}
